import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import LiveTicketScanner from '../utils/liveTicketScanner';
import OfferEditor from '../components/pro/vendor/OfferEditor';
import MenuImportSheet, { ProvenanceLine } from '../components/pro/vendor/MenuImportSheet';
import PageEventsPanel from '../components/pro/PageEventsPanel';
import ProClubSettings from '../components/pro/ProClubSettings';
import PageTabsSettings from '../components/pro/PageTabsSettings';
import EmbedPanel from '../components/pro/EmbedPanel';
import RichAnalytics from '../components/pro/analytics/RichAnalytics';
import DayAxis from '../components/pro/analytics/DayAxis';
import ContactsPanel from '../components/audience/ContactsPanel';
import BroadcastsPanel, { useBroadcastTakeover } from '../components/audience/BroadcastsPanel';
import SendingFrom from '../components/broadcast/SendingFrom';
import ClosedProPages from '../components/pro/ClosedProPages';
import ProPageEditor from '../components/pro/setup/ProPageEditor';
import RolesAccessPanel from '../components/pro/RolesAccessPanel';
import TransferOwnershipPanel from '../components/pro/TransferOwnershipPanel';
import BookingsPanel from '../components/pro/BookingsPanel';
import { setupRows } from '../components/pro/setup/proSetupSteps';
import { useAuth } from '../context/AuthContext';
import { useUser } from '../context/UserContext';
import { SurfacePage, SoftSurface } from '../components/surface';
import { ClubPanel, PanelHead, PillButton, RingTab, TagPill, Toast, useArmed, useToast, INK, MUTED, SUBTLE, BODY, CHIP, INSET, CYAN, RED, RULE, ringOn, label as lbl } from '../components/club/clubUi';
import { useDialog } from '../context/DialogContext';
import { proService, vendorService, audienceService } from '../services';
import { money, hhmm, monthDay, priceLabel } from '../components/pro/vendor/vendorUi';
import useMediaQuery from '../utils/useMediaQuery';
import { getActivePage, setActivePage, isActivePage } from '../services/activePage';

const TINT = { green: '#F0FDF4', yellow: '#FEFCE8', red: '#FEF2F2', grey: '#F9FAFB' };
const EDGE = { green: '#22C55E', yellow: '#EAB308', red: '#EF4444', grey: '#9CA3AF' };
const MAGENTA = '#FF00AA';

/** The six answers a scan can give (Offers Ops O6). Colour carries it; text is #1A1A1A on every tone. */
export function verdictView(r) {
  const who = r.guestName ? `${r.guestName} · claimed ${monthDay(r.claimedAt)}` : null;
  switch (r.verdict) {
    case 'verified': return { title: 'VERIFIED', tone: 'green', who, line: r.offerTitle, sub: 'Take it off the bill. Portal charges nothing and takes nothing — this is between you and the guest.', action: 'MARK USED' };
    case 'already_used': return { title: 'ALREADY USED', tone: 'yellow', who: r.guestName ? `${r.guestName} · used ${hhmm(r.usedAt)}` : null, line: r.offerTitle, sub: `This code was spent ${r.usedAt ? minutesAgo(r.usedAt) : 'already'}${r.usedByName ? ` by ${r.usedByName}` : ''}. One per guest.`, action: 'SCAN NEXT' };
    case 'expired': return { title: 'EXPIRED', tone: 'red', who, line: r.offerTitle, sub: 'The offer has ended. Honour it if you want — that is your call, not ours.', action: 'SCAN NEXT' };
    case 'not_your_offer': return { title: 'NOT YOUR OFFER', tone: 'red', who: null, line: r.offerTitle || 'Another business’s offer', sub: 'This belongs to another business. Nothing has been marked.', action: 'SCAN NEXT' };
    case 'thats_a_ticket': return { title: 'THAT’S A TICKET', tone: 'red', who: null, line: 'Event ticket, not an offer', sub: 'Scan tickets from the event ops page. Same camera, different door.', action: 'SCAN NEXT' };
    case 'ran_out': return { title: 'RAN OUT', tone: 'grey', who: null, line: r.offerTitle, sub: 'The offer stopped itself; nobody can claim it now.', action: 'SCAN NEXT' };
    default: return { title: 'NOT FOUND', tone: 'grey', who: null, line: 'No claim with that code', sub: 'Check the four characters after the dash, or ask the guest to open the offer on their phone.', action: 'SCAN NEXT' };
  }
}
const minutesAgo = (iso) => { const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000)); return m < 60 ? `${m} minute${m === 1 ? '' : 's'} ago` : `${Math.round(m / 60)} hour${Math.round(m / 60) === 1 ? '' : 's'} ago`; };

// EDIT PAGE sits after PAGE and is visible on the phone (design_handoff_pro_setup README §2).
// BOOKINGS (Nov 2 2026): every offer / booking the page received, beside OFFERS; members see it too.
const TABS = [['page', 'PAGE'], ['edit', 'EDIT PAGE'], ['menu', 'MENU'], ['offers', 'OFFERS'], ['bookings', 'BOOKINGS'], ['analytics', 'ANALYTICS'], ['contacts', 'CONTACTS'], ['send', 'SEND'], ['clubs', 'CLUBS'], ['settings', 'SETTINGS']];
const MOB_TABS = ['offers', 'bookings', 'edit', 'analytics', 'contacts', 'send', 'menu', 'clubs', 'settings'];
/** `#edit` / `#edit-photos` (the handoff's deep links) → `?tab=edit&section=photos`. */
const hashToParams = (hash) => { const [h0, ...rest] = (hash || '').replace(/^#/, '').split('-'); return h0 ? { tab: h0, section: rest.join('-') || null } : null; };
const ROLE_BG = { owner: CYAN, admin: INK, staff: CHIP };
const ROLE_FG = { owner: INK, admin: '#fff', staff: INK };
const PHASE_PILL = { live: ['LIVE', '#22C55E', '#fff'], ran_out: ['RAN OUT', '#EAB308', '#000'], draft: ['DRAFT', '#fff', MUTED], ended: ['ENDED', '#E5E7EB', '#000'] };
const rule = (i) => (i ? RULE : 'none');

const ScanGlyph = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" aria-hidden="true"><path d="M3 8V3h5M16 3h5v5M21 16v5h-5M8 21H3v-5M7 12h10" /></svg>
);

/** A soft stat tile: the number on top, the label under it. */
function StatTile({ n, k, phone = false, r = 24 }) {
  return (
    <div className="flex flex-col font-mono" style={{ justifyContent: phone ? 'center' : 'space-between', alignItems: phone ? 'center' : 'stretch', gap: phone ? 6 : 12, padding: phone ? '14px 6px' : '20px 22px', borderRadius: phone ? 20 : r, background: '#fff', textAlign: phone ? 'center' : 'left', minWidth: 0 }}>
      <span style={{ fontSize: phone ? 20 : 30, fontWeight: 800, lineHeight: 1 }}>{n}</span>
      <span style={{ fontSize: phone ? 8 : 9, fontWeight: 700, letterSpacing: phone ? '.12em' : '.16em', color: MUTED, textTransform: 'uppercase' }}>{k}</span>
    </div>
  );
}

/**
 * /pro/dashboard — PAGE · MENU · OFFERS · ANALYTICS · CONTACTS · SEND · SETTINGS, on the Portal
 * vocabulary (design_handoff_portal_landing_rail_pro/Pro Dashboard.dc.html): borderless white
 * panels on #F5F5F5, pill controls, the rainbow ring for the active tab and SCAN AN OFFER, a
 * two-step confirm for closing the page and a black-pill toast. The phone layout is the
 * primary one: SCAN AN OFFER, three tiles, SCANNED TODAY, LIVE OFFERS.
 */
export default function ProDashboardPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { showAlert } = useDialog();
  const phone = useMediaQuery('(max-width: 639px)');
  const mid = useMediaQuery('(max-width: 899px)');
  const { user } = useAuth();
  const { profile } = useUser();
  const tab = params.get('tab') || 'offers';
  const section = params.get('section') || null;
  const setTab = (t, sec = null) => { const n = new URLSearchParams(params); n.set('tab', t); if (sec) n.set('section', sec); else n.delete('section'); setParams(n); };
  // A `#edit-photos` link (notifications, the old prototype) lands on the same tab.
  useEffect(() => {
    const h = hashToParams(window.location.hash);
    if (h && TABS.some(([k]) => k === h.tab)) { const n = new URLSearchParams(params); n.set('tab', h.tab); if (h.section) n.set('section', h.section); setParams(n, { replace: true }); window.history.replaceState(null, '', window.location.pathname + '?' + n.toString()); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { toast, say } = useToast();
  const { armed, confirm, disarm } = useArmed(6000);

  const [biz, setBiz] = useState(null);
  const [offers, setOffers] = useState([]);
  const [dash, setDash] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [importing, setImporting] = useState(false);
  const [closing, setClosing] = useState(false);
  // Every page this account manages; two or more puts a switcher in the header band.
  const [pages, setPages] = useState([]);
  const [switching, setSwitching] = useState(false);
  // ROLES & ACCESS and TRANSFER OWNERSHIP share the managers list.
  const [members, setMembers] = useState(null);
  const sendTakeover = useBroadcastTakeover();

  const load = useCallback(async () => {
    // The API resolves "my page" from the X-Pro-Page header (services/api.js), so every
    // read here already belongs to the page being acted as.
    const [b, o, d, m] = await Promise.all([proService.getMine(), vendorService.myOffers(), vendorService.analytics(days), proService.listMine()]);
    if (b.success) setBiz(b.data);
    if (o.success) setOffers(o.data || []);
    if (d.success) setDash(d.data);
    const list = m?.success ? (m.data || []) : [];
    setPages(list);
    // Several pages and no persona yet: pin the one the API resolved, so this header
    // and the chrome's "Acting as" strip agree on which page is being managed.
    if (list.length >= 2 && b.success && b.data?.slug && !getActivePage()) {
      setActivePage(list.find((p) => p.id === b.data.id) || b.data);
    }
    setLoading(false);
    setSwitching(false);
  }, [days]);
  useEffect(() => { load(); }, [load]);
  const switchPage = (p) => { if (isActivePage(p)) return; setActivePage(p); setSwitching(true); load(); say(`NOW MANAGING ${(p.name || '').toUpperCase()}`); };

  // CONTACTS + SEND are for whoever manages the page (owner / admin), not plain staff.
  const myRole = biz?.viewerRole || pages.find((p) => p.id === biz?.id)?.role || 'owner';
  const managesAudience = myRole === 'owner' || myRole === 'admin';
  // EDIT PAGE, CONTACTS and SEND are for whoever manages the page (owner / admin); a member scans.
  const tabs = (phone ? MOB_TABS.map((k) => TABS.find(([key]) => key === k)) : TABS).filter(([k]) => managesAudience || (k !== 'contacts' && k !== 'send' && k !== 'edit' && k !== 'clubs'));
  // EMAIL in SETTINGS: the page's sending address, replies and own domain — the same card the email builder carries.
  const emailClient = useMemo(() => (biz?.id ? audienceService.broadcastClient('pro', biz.id) : null), [biz?.id]);
  const audienceOwner = biz ? { ownerType: 'pro', ownerId: biz.id, name: biz.name, publicPath: `/pro/${biz.slug}`, followerWord: 'FOLLOWERS', logoUrl: biz.logoUrl || null, roleTag: String(biz.viewerRole || 'owner').toUpperCase() } : null;

  const r = phone ? 22 : 24;
  const pad = phone ? '18px 16px' : '22px 24px';
  const gap = phone ? 10 : 16;
  const frame = (children) => (
    <SoftSurface>
      <SurfacePage bare showTopBarOnMobile>
        <main className="mx-auto flex w-full flex-col font-mono" style={{ maxWidth: 1180, padding: phone ? '12px 12px 24px' : '24px 32px 72px', gap, boxSizing: 'border-box', color: INK }} data-testid="pro-dashboard">
          {children}
        </main>
        <Toast text={toast} phone={phone} bottom={phone ? 150 : 32} />
      </SurfacePage>
    </SoftSurface>
  );

  const closePage = async () => {
    if (!confirm('close-page')) return;
    setClosing(true);
    const res = await proService.closePage(biz.id);
    setClosing(false);
    if (!res.success) { await showAlert({ title: 'COULD NOT CLOSE', message: res.error || 'Try again.', type: 'error' }); return; }
    setActivePage(null);
    setBiz(null);
    setLoading(true);
    say('PAGE CLOSED · 30 DAYS TO BRING IT BACK');
    load();
  };

  if (loading) return frame(<div className="py-24 text-center"><LoadingSpinner label="LOADING…" /></div>);
  if (!biz) return frame(
    <ClubPanel phone={phone} style={{ alignItems: 'center', textAlign: 'center', gap: 16 }}>
      <span style={lbl(14, '.1em')}>NO PRO PAGE YET</span>
      <PillButton size={48} onClick={() => navigate('/pro/setup')}>SET ONE UP →</PillButton>
      <div className="w-full text-left"><ClosedProPages onRestored={() => { setLoading(true); load(); }} /></div>
    </ClubPanel>,
  );

  const live = offers.filter((o) => o.phase === 'live' || o.phase === 'ran_out');
  const scanned = dash?.scannedToday || [];
  const offBillsToday = scanned.reduce((n, c) => n + (c.valueCents || 0), 0);
  const claimedAll = offers.reduce((n, o) => n + (o.claimedCount || 0), 0);
  const usedAll = offers.reduce((n, o) => n + (o.redeemedCount || 0), 0);
  const findStat = (group, key) => (dash?.[group] || []).find((s) => s.key === key);
  const closeArmed = armed === 'close-page';
  const bookableKind = !biz.kind || biz.kind === 'bookable';
  const menuTitle = biz.kind === 'bar' ? 'DRINKS' : biz.kind === 'shop' || bookableKind ? 'SERVICES' : 'MENU';
  const sections = biz.menuSections || [];
  const itemCount = sections.reduce((n, s) => n + (s.items?.length || 0), 0);
  const canImport = !bookableKind && biz.kind !== 'venue';

  const scanButton = phone ? (
    <button type="button" onClick={() => setScanning(true)} data-testid="scan-button" className="font-mono" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, height: 76, border: '3px solid transparent', borderRadius: 999, background: ringOn, color: '#fff', fontSize: 15, fontWeight: 800, letterSpacing: '.14em', cursor: 'pointer' }}>
      <ScanGlyph size={24} />SCAN AN OFFER
    </button>
  ) : (
    <button type="button" onClick={() => setScanning(true)} data-testid="scan-button" className="font-mono hover:opacity-90" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, minHeight: 104, border: '3px solid transparent', borderRadius: r, background: ringOn, color: '#fff', fontSize: 13, fontWeight: 800, letterSpacing: '.16em', cursor: 'pointer', transition: 'opacity .2s' }}>
      <ScanGlyph />SCAN AN OFFER
    </button>
  );

  const offersTab = (
    <>
      {phone ? (
        <>
          {scanButton}
          <div className="grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 8 }}>
            {[[String(scanned.length), 'SCANNED TODAY'], [String(claimedAll), 'CLAIMED'], [money(offBillsToday, { whole: true }), 'OFF BILLS TODAY']].map(([n, k]) => <StatTile key={k} n={n} k={k} phone />)}
          </div>
        </>
      ) : (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0,1fr)) minmax(200px, 1.15fr)', gap }}>
          {[[String(claimedAll), 'CLAIMED · ALL TIME'], [String(usedAll), 'WALKED IN WITH IT'], [`${Math.round(((findStat('offers', 'redemption_rate')?.value) || 0) * 100)}%`, 'REDEMPTION RATE'], [money(findStat('offers', 'value_given')?.value || 0, { whole: true }), 'GIVEN AWAY']].map(([n, k]) => <StatTile key={k} n={n} k={k} r={r} />)}
          {scanButton}
        </div>
      )}

      <ClubPanel phone={phone} style={{ gap: 6 }} data-testid="scanned-today">
        <PanelHead title="SCANNED TODAY" right={phone ? null : 'NEWEST FIRST'} />
        {scanned.length === 0 && <span style={{ padding: 18, borderRadius: 18, background: INSET, fontSize: 12, color: MUTED }}>Nothing scanned yet today.</span>}
        {scanned.map((c, i) => (
          <div key={c.id} className="flex items-center" style={{ gap: 12, padding: '11px 0', borderTop: rule(i) }}>
            <span style={{ flex: '0 0 52px', fontSize: 12, fontWeight: 800 }}>{hhmm(c.usedAt)}</span>
            <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 3 }}>
              <span className="truncate" style={{ fontSize: 13 }}>{c.title}</span>
              <span style={{ fontSize: 10, letterSpacing: '.12em', color: MUTED }}><span>{c.code}</span>{c.businessName && <span> · {c.businessName}</span>}</span>
            </span>
            <TagPill bg="#DCFCE7" fg="#166534" size={8} padding="4px 10px">USED</TagPill>
          </div>
        ))}
      </ClubPanel>

      <ClubPanel phone={phone} style={{ gap: 4 }} data-testid="live-offers">
        <span style={{ ...lbl(12, '.16em'), paddingBottom: 8 }}>LIVE OFFERS</span>
        {!phone && (
          <div className="grid" style={{ gridTemplateColumns: 'minmax(0,1fr) 130px 84px 70px 110px 96px', gap: 12, padding: '10px 16px', borderRadius: 999, background: INSET, fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: MUTED }}>
            <span>OFFER</span><span>USAGE</span><span className="text-right">CLAIMED</span><span className="text-right">USED</span><span className="text-right">GIVEN AWAY</span><span className="text-right">STATUS</span>
          </div>
        )}
        {live.length === 0 && <span style={{ padding: 18, borderRadius: 18, background: INSET, fontSize: 12, color: MUTED, marginTop: 6 }}>No live offers. Post one below.</span>}
        {live.map((o, i) => {
          const usage = o.usageMode === 'unlimited_per_guest' ? 'UNLIMITED' : 'ONE PER GUEST';
          const [ph, bg, fg] = PHASE_PILL[o.phase] || PHASE_PILL.live;
          return phone ? (
            <div key={o.id} className="flex items-center" style={{ gap: 10, padding: '12px 0', borderTop: rule(i) }}>
              <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 4 }}>
                <span style={{ fontSize: 13, lineHeight: 1.3 }}>{o.title}</span>
                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.1em', color: MUTED }}>{usage}</span>
              </span>
              <span className="flex flex-col items-end" style={{ gap: 3 }}>
                <span style={{ fontSize: 13, fontWeight: 800 }}>{o.redeemedCount} / {o.claimedCount}</span>
                <span style={{ fontSize: 8, letterSpacing: '.1em', color: SUBTLE }}>USED / CLAIMED</span>
              </span>
            </div>
          ) : (
            <div key={o.id} className="grid items-center" style={{ gridTemplateColumns: 'minmax(0,1fr) 130px 84px 70px 110px 96px', gap: 12, padding: '13px 16px', borderTop: rule(i) }}>
              <span style={{ fontSize: 13, lineHeight: 1.35 }}>{o.title}</span>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.12em', color: MUTED }}>{usage}</span>
              <span className="text-right" style={{ fontSize: 14, fontWeight: 800 }}>{o.claimedCount}</span>
              <span className="text-right" style={{ fontSize: 14, fontWeight: 800 }}>{o.redeemedCount}</span>
              <span className="text-right" style={{ fontSize: 14, fontWeight: 800 }}>{money((o.valueCents || 0) * (o.redeemedCount || 0))}</span>
              <span className="flex justify-end"><TagPill bg={bg} fg={fg} size={8} padding="4px 10px">{ph}</TagPill></span>
            </div>
          );
        })}
      </ClubPanel>

      <OfferEditor offers={offers} onChanged={load} mobile={phone} say={say} />
    </>
  );

  const menuTab = (
    <ClubPanel phone={phone} style={{ gap: 16 }}>
      <PanelHead title={menuTitle}>
        {canImport && <PillButton variant="secondary" size={36} onClick={() => setImporting(true)} data-testid="import-from-website">IMPORT FROM WEBSITE</PillButton>}
      </PanelHead>
      {sections.length > 0 && (
        <div className="grid" style={{ gridTemplateColumns: phone ? 'minmax(0,1fr)' : `repeat(${Math.min(3, sections.length)}, minmax(0,1fr))`, gap: 10 }}>
          {sections.map((s, i) => (
            <div key={s.id || i} className="flex flex-col" style={{ gap: 10, padding: '16px 18px', borderRadius: 20, background: INSET }}>
              <span className="flex justify-between" style={{ gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' }}>{s.name || 'MENU'}</span>
                <span style={{ fontSize: 10, color: MUTED }}>{(s.items || []).length} ITEMS</span>
              </span>
              {(s.items || []).slice(0, 6).map((it, j) => (
                <span key={it.id || j} className="flex justify-between" style={{ gap: 10, fontSize: 12 }}>
                  <span className="truncate">{it.name}</span>
                  <span className="flex-shrink-0" style={{ fontWeight: 800 }}>{priceLabel(it) || '—'}</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      )}
      <p className="m-0" style={{ fontSize: 13, lineHeight: 1.6 }}>{itemCount} items in {sections.length} sections. Sections, photos, layouts and TOP THINGS live in the page editor.</p>
      <span className="flex flex-wrap items-center" style={{ gap: 14 }}>
        <PillButton size={48} onClick={() => setTab('edit', 'list')}>OPEN THE EDITOR →</PillButton>
        <ProvenanceLine url={biz.menuImportUrl} at={biz.menuImportedAt} onAgain={() => setImporting(true)} />
      </span>
    </ClubPanel>
  );

  const pageTab = (
    <>
      <ClubPanel phone={phone} style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        <PillButton variant="secondary" size={48} onClick={() => navigate(`/pro/${biz.slug}`)}>VIEW YOUR PAGE →</PillButton>
        <PillButton size={48} onClick={() => setTab('edit')}>EDIT YOUR PAGE</PillButton>
        {bookableKind && <PillButton variant="secondary" size={48} onClick={() => navigate('/pro/desk')}>BOOKING DESK</PillButton>}
      </ClubPanel>
      <ClubPanel phone={phone} style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
        <span className="flex flex-col" style={{ gap: 4 }}>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.16em', color: MUTED }}>KIND</span>
          <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' }}>{biz.kind || 'bookable'}</span>
        </span>
        <span className="flex items-center" style={{ flex: '1 1 260px', gap: 10, padding: '12px 16px', borderRadius: 16, background: biz.isPublished ? '#F0FDF4' : '#FEFCE8' }}>
          <span className="flex-shrink-0 rounded-full" style={{ width: 8, height: 8, background: biz.isPublished ? '#22C55E' : '#EAB308' }} />
          <span style={{ fontSize: 12, lineHeight: 1.5 }}>{biz.isPublished ? 'Published. People can find you in the directory.' : 'Not published yet — finish setup to go live.'}</span>
        </span>
      </ClubPanel>
      <PageEventsPanel pageId={biz.id} phone={phone} />
    </>
  );

  const settingsTab = (
    <>
      <ClubPanel phone={phone} style={{ gap: 2 }}>
        <span style={{ ...lbl(12, '.16em'), paddingBottom: 8 }}>SET UP</span>
        {[...setupRows(biz.kind || 'bookable'), ...(managesAudience ? [['CLUBS', 'Link a club you own or manage to this page.', 'clubs']] : [])].map(([t, d, sec], i) => (
          <button key={t} type="button" onClick={() => sec === 'clubs' ? setTab('clubs') : setTab('edit', sec)} className="flex items-center text-left font-mono hover:opacity-70" style={{ gap: 14, padding: '14px 0', border: 0, borderTop: rule(i), background: 'none', cursor: 'pointer', color: INK, transition: 'opacity .2s' }} data-testid={`setup-row-${sec}`}>
            <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '.12em' }}>{t}</span>
              <span style={{ fontSize: 12, color: MUTED }}>{d}</span>
            </span>
            <span className="flex flex-shrink-0 items-center justify-center rounded-full" style={{ width: 36, height: 36, background: CHIP }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="square" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
            </span>
          </button>
        ))}
      </ClubPanel>
      <PageTabsSettings pageId={biz.id} kind={biz.kind} slug={biz.slug} hiddenTabs={biz.hiddenTabs} tabOrder={biz.tabOrder} defaultTab={biz.defaultTab} linkedClub={!!biz.linkedClub} phone={phone}
        onSaved={(t) => setBiz((b) => (b ? { ...b, ...(t.hiddenTabs ? { hiddenTabs: t.hiddenTabs } : {}), ...(t.tabOrder ? { tabOrder: t.tabOrder } : {}), ...('defaultTab' in t ? { defaultTab: t.defaultTab || null } : {}) } : b))} />
      {!biz.isListed && <EmbedPanel slug={biz.slug} pageName={biz.name} phone={phone} say={say} />}
      {managesAudience && !biz.isListed && (
        <>
          {emailClient && (
            <ClubPanel phone={phone} style={{ gap: 12 }} data-testid="email-settings-card">
              <span style={lbl(12, '.16em')}>EMAIL</span>
              <span style={{ fontSize: 12, lineHeight: 1.65, color: MUTED }}>Where your page's email comes from and where replies land. Send from your own domain once its records are in.</span>
              <SendingFrom client={emailClient} />
            </ClubPanel>
          )}
          <RolesAccessPanel slug={biz.slug} currentUserId={profile?.userId || user?.id} phone={phone} say={say} onChanged={setMembers} />
          <TransferOwnershipPanel slug={biz.slug} pageName={biz.name} currentUserId={profile?.userId || user?.id} phone={phone} say={say} members={members} onTransferred={() => { setLoading(true); load(); }} />
        </>
      )}
      {myRole === 'owner' && !biz.isListed && (
        <ClubPanel phone={phone} style={{ gap: 12, boxShadow: `inset 0 0 0 2px ${RED}` }} data-testid="close-page-card">
          <span style={{ ...lbl(12, '.16em'), color: RED }}>CLOSE THIS PAGE</span>
          <span style={{ fontSize: 12, lineHeight: 1.65, color: BODY }}>
            {closeArmed
              ? `"${biz.name}" comes off Portal now — the directory, search, its events, contacts and sends, all hidden. You have 30 days to bring it back from the dashboard; after that it is gone for good.`
              : 'It comes off Portal at once — directory, search, its events, contacts and sends. You have 30 days to bring it back; then it is gone for good. Open bookings and an active subscription have to be settled first.'}
          </span>
          <span className="flex flex-wrap" style={{ gap: 6 }}>
            <PillButton variant={closeArmed ? 'armed' : 'dangerGhost'} size={46} onClick={closePage} disabled={closing} style={closeArmed ? undefined : { boxShadow: `inset 0 0 0 2px ${RED}` }} data-testid="close-page">
              {closing ? 'CLOSING…' : closeArmed ? 'YES, CLOSE IT' : 'CLOSE THIS PAGE'}
            </PillButton>
            {closeArmed && <PillButton variant="tertiary" size={46} weight={700} onClick={disarm}>KEEP IT</PillButton>}
          </span>
        </ClubPanel>
      )}
      <ClosedProPages phone={phone} onRestored={(p) => { if (p) setActivePage(p); setLoading(true); say(`${(p?.name || 'YOUR PAGE').toUpperCase()} IS BACK`); load(); }} />
    </>
  );

  const body = tab === 'offers' ? offersTab
    : tab === 'bookings' ? <BookingsPanel key={biz.id} pageId={biz.id} phone={phone} />
    : tab === 'analytics' ? <AnalyticsTab dash={dash} days={days} setDays={setDays} phone={phone} mid={mid} r={r} pad={pad} gap={gap} />
    : tab === 'contacts' && audienceOwner ? <div className="flex flex-col" style={{ gap }} data-testid="contacts-tab"><ContactsPanel owner={audienceOwner} mobile={phone} /></div>
    : tab === 'send' && audienceOwner ? <div className="flex flex-col" style={{ gap }} data-testid="send-tab"><BroadcastsPanel owner={audienceOwner} perms={{ canEdit: true, canCreate: true, canSend: true }} mobile={phone} /></div>
    : tab === 'menu' ? menuTab
    : tab === 'edit' && managesAudience ? <div className="flex min-w-0 flex-col" style={{ gap }} data-testid="edit-tab"><ProPageEditor key={biz.id} mode="edit" chrome={false} section={section} onSectionChange={(sec) => { const n = new URLSearchParams(params); n.set('tab', 'edit'); n.set('section', sec); setParams(n, { replace: true }); }} barBottom={phone ? 84 : 16} onOpenTab={(t) => setTab(t)} /></div>
    : tab === 'page' ? pageTab
    : tab === 'clubs' && managesAudience ? <ProClubSettings key={biz.id} pageId={biz.id} linkedClub={biz.linkedClub} phone={phone} onSaved={(linkedClub) => setBiz((b) => ({ ...b, linkedClub }))} />
    : tab === 'settings' ? settingsTab
    : null;

  return frame(
    <>
      {!sendTakeover && (
        <section className="flex flex-wrap items-center bg-white" style={{ gap: 18, padding: phone ? 14 : '18px 22px', borderRadius: r }} data-testid="dash-header">
          <span className="flex-shrink-0 overflow-hidden" style={{ width: phone ? 52 : 64, height: phone ? 52 : 64, borderRadius: 18, background: INSET }}>
            {biz.logoUrl
              ? <img src={biz.logoUrl} alt="" className="block h-full w-full object-cover" />
              : <span className="flex h-full w-full items-center justify-center" style={{ fontSize: phone ? 22 : 28, fontWeight: 800 }}>{(biz.name || '?').trim().charAt(0).toUpperCase()}</span>}
          </span>
          <span className="flex min-w-0 flex-col" style={{ flex: '1 1 260px', gap: 6 }}>
            <span className="flex flex-wrap items-center" style={{ gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.18em', color: MUTED }}>{phone ? `${tab.toUpperCase()} · TODAY` : 'PRO DASHBOARD'}</span>
              <TagPill bg={ROLE_BG[myRole] || CHIP} fg={ROLE_FG[myRole] || INK} size={9} padding="4px 10px" style={{ letterSpacing: '.16em' }} data-testid="role-pill">{myRole.toUpperCase()}</TagPill>
              {biz.followerCount != null && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: SUBTLE }}>{biz.followerCount} FOLLOWER{biz.followerCount === 1 ? '' : 'S'}</span>}
            </span>
            <h1 className="m-0" style={{ fontSize: phone ? 20 : 28, fontWeight: 800, letterSpacing: '.04em', lineHeight: 1.1, overflowWrap: 'anywhere' }}>{biz.name}</h1>
          </span>
          {/* Page switcher — only when the account manages more than one page. Picking one
              sets the persona; the reload lands on that page's data via the header. */}
          <div className="flex flex-wrap items-center" style={{ gap: 6, opacity: switching ? 0.5 : 1 }} data-testid="page-switcher">
            {pages.length >= 2 && pages.map((p) => {
              const on = biz.id ? p.id === biz.id : isActivePage(p);
              return (
                <button key={p.id || p.slug} type="button" onClick={() => switchPage(p)} aria-pressed={on} disabled={switching} data-testid={`page-switch-${p.id || p.slug}`}
                  className="font-mono truncate" style={{ maxWidth: 220, height: 36, padding: '0 16px', border: '2px solid transparent', borderRadius: 999, background: on ? INK : CHIP, color: on ? '#fff' : INK, fontSize: 10, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase', cursor: 'pointer', transition: 'background-color .2s, color .2s' }}>
                  {p.name}
                </button>
              );
            })}
            <button type="button" onClick={() => navigate('/pro/setup?new=1')} data-testid="new-pro-page"
              className="font-mono hover:bg-black hover:text-white" style={{ display: 'flex', alignItems: 'center', height: 36, padding: '0 16px', border: '2px dashed #000', borderRadius: 999, background: 'transparent', fontSize: 10, fontWeight: 800, letterSpacing: '.12em', cursor: 'pointer', transition: 'background-color .2s, color .2s' }}>
              + NEW PRO PAGE
            </button>
          </div>
        </section>
      )}

      {!sendTakeover && (
        <div role="tablist" aria-label="Pro dashboard" className="flex" style={{ flexWrap: phone ? 'nowrap' : 'wrap', gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }} data-testid="dash-tabs">
          {tabs.map(([k, l]) => <RingTab key={k} role="tab" on={tab === k} onClick={() => setTab(k)} data-testid={`tab-${k}`}>{phone ? k : l}</RingTab>)}
        </div>
      )}

      <div className="flex min-w-0 flex-col" style={{ gap }}>{body}</div>

      {importing && (
        <MenuImportSheet kind={biz.kind} savedUrl={biz.menuImportUrl || biz.listedMenuLink || biz.websiteUrl || null} existingCount={itemCount} onClose={() => setImporting(false)}
          onConfirm={async (incoming, { mode, url }) => {
            const current = mode === 'replace' ? [] : sections.map((s) => ({ ...s, items: [...(s.items || [])] }));
            incoming.forEach((sec) => { const ex = current.find((s) => s.name.trim().toUpperCase() === sec.name.trim().toUpperCase()); if (ex) ex.items.push(...sec.items); else current.push(sec); });
            await vendorService.saveMenu({ menuLayout: biz.menuLayout, importUrl: url, sections: current.map((sec) => ({ id: sec.id || null, name: sec.name || 'MENU', note: sec.note || null, layout: sec.layout || null, photoPath: sec.photoPath || null, showAbv: !!sec.showAbv, items: sec.items.filter((i) => i.name?.trim()).map((i) => ({ id: i.id || null, name: i.name, description: i.description || null, priceCents: i.priceCents ?? null, priceText: i.priceText || null, marks: i.marks || [], abv: i.abv ?? null, style: i.style || null, durationMinutes: i.durationMinutes ?? null, bookMode: i.bookMode || 'none', photoPath: i.photoPath || null, isTopThing: !!i.isTopThing })) })) });
            const added = incoming.reduce((n, s) => n + (s.items?.length || 0), 0);
            setImporting(false); say(`${added} ITEMS ADDED`); load();
          }}
          onPasteInstead={() => setTab('edit', 'list')} />
      )}

      {scanning && <OfferScanner onClose={() => { setScanning(false); load(); }} />}
    </>,
  );
}

/* ─────────────────────────── SCANNER (Offers Ops O4/O5) ─────────────────────────── */
export function OfferScanner({ onClose }) {
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const lastRef = useRef({ text: '', at: 0 });
  const [result, setResult] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [camError, setCamError] = useState('');

  const check = useCallback(async (payload) => {
    setBusy(true);
    const r = await vendorService.redeem(payload, false);
    setBusy(false);
    setResult(r.success ? r.data : { verdict: 'not_found' });
    try { if (navigator.vibrate) navigator.vibrate(r.success && r.data.verdict === 'verified' ? 60 : 30); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    let s;
    (async () => {
      try {
        s = new LiveTicketScanner(videoRef.current, (text) => {
          const now = Date.now();
          if (lastRef.current.text === text && now - lastRef.current.at < 3000) return;
          lastRef.current = { text, at: now };
          if (!result) check({ raw: text });
        });
        scannerRef.current = s;
        await s.start();
      } catch (e) { setCamError('Camera unavailable — type the code instead.'); }
    })();
    return () => { try { s?.stop(); } catch { /* noop */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markUsed = async () => {
    if (!result?.claimId) return;
    setBusy(true);
    const r = await vendorService.redeem({ claimId: result.claimId }, true);
    setBusy(false);
    setResult(r.success ? { ...r.data, marked: r.data.verdict === 'verified' } : result);
  };
  const v = result ? verdictView(result) : null;
  const canMark = result?.verdict === 'verified' && !result.marked;
  const darkPill = { height: 40, padding: '0 18px', border: 'none', borderRadius: 999, background: '#262626', color: '#fff', fontFamily: 'inherit', fontSize: 11, fontWeight: 800, letterSpacing: '.14em', cursor: 'pointer' };

  return (
    <div className="fixed inset-0 safe-inset z-[9999] flex flex-col font-mono text-white" style={{ background: '#0A0A0A' }} role="dialog" aria-modal="true" aria-label="Scan offer QR" data-testid="offer-scanner">
      <div className="pt-safe-top flex items-center justify-between" style={{ padding: '16px 20px' }}>
        <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: '.08em' }}>SCAN OFFER QR</span>
        <button type="button" onClick={onClose} style={darkPill}>CLOSE</button>
      </div>
      <div className="relative flex flex-1 items-center justify-center overflow-hidden" style={{ margin: '0 20px', borderRadius: 28, minHeight: 240, background: 'radial-gradient(circle at 50% 45%, #1C1C1C, #050505)' }}>
        <video ref={videoRef} className="absolute inset-0 h-full w-full object-cover" playsInline muted />
        <div className="relative" style={{ width: 240, height: 240, borderRadius: 28, boxShadow: `0 0 0 2px ${result ? '#6B7280' : '#fff'}`, zIndex: 1 }}>
          {!result && <span className="pr-scan-line absolute" style={{ left: '8%', right: '8%', height: 2, borderRadius: 2, background: CYAN, boxShadow: `0 0 12px ${CYAN}` }} aria-hidden="true" />}
        </div>
        {camError && <span className="absolute bottom-4 px-3 text-center" style={{ fontSize: 11, color: SUBTLE, zIndex: 1 }}>{camError}</span>}
      </div>
      {!result && (
        <div className="flex flex-col" style={{ padding: '18px 20px 22px', gap: 12 }}>
          <span className="text-center" style={{ fontSize: 12, color: SUBTLE }}>Point your camera at an offer QR code</span>
          <span className="flex items-center" style={{ gap: 8, padding: '6px 6px 6px 18px', borderRadius: 999, background: '#1A1A1A' }}>
            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.14em', color: MUTED }}>OR TYPE IT</span>
            <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="BV-0000" aria-label="Offer code" className="min-w-0 flex-1 font-mono placeholder:text-gray-600" style={{ height: 44, padding: '0 16px', border: 'none', borderRadius: 999, background: '#000', color: '#fff', fontSize: 15, fontWeight: 800, letterSpacing: '.14em', outline: 'none', boxShadow: 'none' }} />
            <button type="button" onClick={() => code.trim() && check({ code: code.trim() })} disabled={busy} className="font-mono disabled:opacity-50" style={{ height: 44, padding: '0 18px', border: 'none', borderRadius: 999, background: '#fff', color: '#000', fontSize: 11, fontWeight: 800, letterSpacing: '.12em', cursor: 'pointer' }}>CHECK</button>
          </span>
        </div>
      )}
      {v && (
        <div className="overflow-hidden bg-white pb-safe-bot" style={{ margin: '14px 12px 12px', borderRadius: 28, color: '#1A1A1A' }} data-testid={`verdict-${result.verdict}`}>
          <div className="flex flex-col" style={{ gap: 6, padding: '20px 22px', background: TINT[v.tone] }}>
            <span className="flex items-center" style={{ gap: 10 }}>
              <span className="rounded-full" style={{ width: 12, height: 12, background: EDGE[v.tone] }} />
              <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: '.06em' }}>{result.marked ? 'MARKED USED' : v.title}</span>
            </span>
            <span style={{ fontSize: 13, color: '#4B5563' }}>{v.who || '—'}</span>
          </div>
          <div className="flex flex-col" style={{ gap: 10, padding: '18px 22px 22px' }}>
            <span style={{ fontSize: 17, lineHeight: 1.35 }}>{v.line}</span>
            <span className="flex justify-between" style={{ gap: 10, fontSize: 12, fontWeight: 800, letterSpacing: '.14em' }}>
              <span>{result.code || '—'}</span>
              {result.valueText && <span>{result.valueText.toUpperCase()}</span>}
            </span>
            <span style={{ fontSize: 12, lineHeight: 1.55, color: MUTED }}>{v.sub}</span>
            <span className="flex" style={{ gap: 8, paddingTop: 4 }}>
              {canMark && <button type="button" onClick={markUsed} disabled={busy} className="font-mono disabled:opacity-50" style={{ flex: 1, height: 54, border: 'none', borderRadius: 999, background: '#000', color: '#fff', fontSize: 13, fontWeight: 800, letterSpacing: '.12em', cursor: 'pointer' }}>MARK USED</button>}
              <button type="button" onClick={() => { setResult(null); setCode(''); }} className="font-mono" style={{ flex: canMark ? '0 0 auto' : 1, height: 54, padding: '0 22px', border: 'none', borderRadius: 999, background: CHIP, color: INK, fontSize: 13, fontWeight: 800, letterSpacing: '.12em', cursor: 'pointer' }}>SCAN NEXT</button>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── ANALYTICS (O8 / O9) ─────────────────────────── */
function AnalyticsTab({ dash, days, setDays, phone, mid, r, pad, gap }) {
  if (!dash) return <div className="py-10"><LoadingSpinner /></div>;
  const find = (g, k) => dash[g]?.find((s) => s.key === k);
  const fmt = (s) => (!s ? '—' : s.format === 'rate' ? `${Math.round(s.value * 100)}%` : s.format === 'cents' ? money(s.value, { whole: true }) : s.format === 'days' ? `${s.value.toFixed(1)}d` : Math.round(s.value).toLocaleString('en-US'));
  const delta = (s) => (s?.delta == null ? null : `${s.delta >= 0 ? '+' : '−'}${Math.abs(Math.round(s.delta * 100))}%`);
  const big = [find('reach', 'views'), find('reach', 'uniques'), find('reach', 'dir_ctr'), find('offers', 'claims')].filter(Boolean);
  const series = phone ? (dash.views || []).slice(-14) : (dash.views || []);
  const maxV = Math.max(1, ...series.map((p) => p.count));
  const budget = 180;
  const sources = Object.entries(dash.sources || {}).sort((a, b) => b[1] - a[1]);
  const maxS = Math.max(1, ...sources.map(([, n]) => n));
  const labelFor = { directory: 'PROS DIRECTORY', planner: 'THE PLANNER', direct: 'DIRECT LINK', event: 'AN EVENT PAGE', search: 'SEARCH', qr: 'A QR IN THE ROOM' };
  const taps = [['OPENED THE MENU', find('interest', 'menu_opens')?.value], ...Object.entries(dash.outbound || {}).map(([k, n]) => [`TAPPED ${k.toUpperCase()}`, n]), ['SAVED THE PAGE', find('interest', 'saves')?.value]].filter(([, n]) => n != null);
  const saw = find('offers', 'offer_views')?.value || 0; const claimed = find('offers', 'claims')?.value || 0; const walked = find('offers', 'redemptions')?.value || 0; const cost = find('offers', 'value_given')?.value || 0;
  const pct = (n) => (saw ? Math.round((n / saw) * 100) : 0);
  const funnel = [
    ['SAW THE OFFER', saw.toLocaleString('en-US'), saw ? 100 : 0, saw ? '100%' : '—', INK],
    ['CLAIMED IT', claimed.toLocaleString('en-US'), pct(claimed), saw ? `${pct(claimed)}%` : '—', INK],
    ['WALKED IN', walked.toLocaleString('en-US'), pct(walked), saw ? `${pct(walked)}%` : '—', MAGENTA],
    ['COST', money(cost, { whole: true }), pct(walked), walked ? `${money(Math.round(cost / walked), { whole: true })} EA` : '—', CYAN],
  ];
  const hours = dash.redemptionsByHour || new Array(24).fill(0);
  const dayHours = hours.map((n, h) => [n, h]).filter(([, h]) => h >= 8 && h <= 23);
  const maxH = Math.max(1, ...dayHours.map(([n]) => n));
  const extras = [['BOOKING', dash.booking], ['VENUE', dash.venue], ['LISTED PAGE', dash.listed]].filter(([, rows]) => rows?.length);
  const panel = (title, children, right, extra = {}) => (
    <section className="flex min-w-0 flex-col bg-white font-mono" style={{ borderRadius: r, padding: pad, gap: 14, ...extra }}>
      <span className="flex flex-wrap items-center justify-between" style={{ gap: 10 }}><span style={lbl(12, '.16em')}>{title}</span>{right}</span>
      {children}
    </section>
  );
  const note = (text) => <span style={{ paddingTop: 10, borderTop: RULE, fontSize: 11, lineHeight: 1.6, color: MUTED }}>{text}</span>;

  return (
    <div className="flex flex-col" style={{ gap }} data-testid="analytics-tab">
      <div className="flex flex-wrap items-center justify-between" style={{ gap: 12 }}>
        <span style={{ fontSize: phone ? 16 : 20, fontWeight: 800, letterSpacing: '.06em' }}>{days >= 3650 ? 'ALL TIME' : `LAST ${days} DAYS`}</span>
        <span role="radiogroup" aria-label="Range" className="flex" style={{ gap: 2, padding: 3, borderRadius: 999, background: '#fff' }}>
          {[[7, '7D'], [30, '30D'], [90, '90D'], [3650, 'ALL']].map(([d, l]) => (
            <button key={d} type="button" role="radio" aria-checked={days === d} onClick={() => setDays(d)} className="font-mono" style={{ height: 38, padding: '0 18px', border: 'none', borderRadius: 999, background: days === d ? INK : 'transparent', color: days === d ? '#fff' : INK, fontSize: 11, fontWeight: 800, letterSpacing: '.1em', cursor: 'pointer' }}>{l}</button>
          ))}
        </span>
      </div>
      <div className="grid" style={{ gridTemplateColumns: `repeat(${phone ? 2 : 4}, minmax(0,1fr))`, gap }}>
        {big.map((s) => {
          const d = delta(s);
          const up = s.delta > 0;
          return (
            <div key={s.key} className="flex flex-col bg-white" style={{ gap: 10, padding: phone ? '16px 16px' : '20px 22px', borderRadius: r }}>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.16em', color: MUTED }}>{s.key === 'dir_ctr' ? 'DIRECTORY CLICK-THRU' : s.label}</span>
              <span className="flex items-baseline" style={{ gap: 10 }}>
                <span style={{ fontSize: phone ? 22 : 32, fontWeight: 800, lineHeight: 1 }}>{fmt(s)}</span>
                {d && <span style={{ padding: '3px 8px', borderRadius: 999, background: up ? '#DCFCE7' : CHIP, color: up ? '#166534' : MUTED, fontSize: 10, fontWeight: 800 }}>{d}</span>}
              </span>
            </div>
          );
        })}
      </div>
      <div className="grid items-stretch" style={{ gridTemplateColumns: mid ? 'minmax(0,1fr)' : 'minmax(0,1fr) 360px', gap }}>
        {panel('PAGE VIEWS BY DAY', (
          <>
            <div className="flex items-end" style={{ flex: 1, minHeight: 200, gap: series.length > 20 ? 3 : 6 }} data-testid="views-chart">
              {series.map((p) => (
                <div key={p.day} className="flex min-w-0 flex-1 flex-col justify-end" style={{ height: '100%', gap: 2 }} title={`${p.day}: ${p.count} views, ${p.uniques} unique`}>
                  <span style={{ height: Math.round(((p.count - p.uniques) / maxV) * budget), borderRadius: '6px 6px 2px 2px', background: INK }} />
                  <span style={{ height: Math.round((p.uniques / maxV) * budget), borderRadius: '2px 2px 6px 6px', background: MAGENTA }} />
                </div>
              ))}
              {series.length === 0 && <span style={{ fontSize: 12, color: MUTED }}>No views yet.</span>}
            </div>
            <DayAxis days={series.map((p) => p.day)} gap={series.length > 20 ? 3 : 6} testId="views-axis" />
          </>
        ), (
          <span className="flex" style={{ gap: 14 }}>
            {[[INK, 'VIEWS'], [MAGENTA, 'UNIQUE']].map(([c, l]) => <span key={l} className="flex items-center" style={{ gap: 6, fontSize: 9, fontWeight: 700, letterSpacing: '.12em', color: MUTED }}><span className="rounded-full" style={{ width: 10, height: 10, background: c }} />{l}</span>)}
          </span>
        ))}
        <div className="flex min-w-0 flex-col" style={{ gap }}>
          {panel('WHERE THEY CAME FROM', (
            <>
              {sources.length === 0 && <span style={{ fontSize: 12, color: MUTED }}>No views yet.</span>}
              {sources.map(([k, n]) => (
                <div key={k} className="flex flex-col" style={{ gap: 6 }}>
                  <span className="flex justify-between" style={{ gap: 10 }}><span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.1em' }}>{labelFor[k] || k.toUpperCase()}</span><span style={{ fontSize: 12, fontWeight: 800 }}>{n}</span></span>
                  <span className="overflow-hidden" style={{ height: 10, borderRadius: 999, background: CHIP }}><span className="block h-full" style={{ width: `${Math.round((n / maxS) * 100)}%`, borderRadius: 999, background: INK }} /></span>
                </div>
              ))}
            </>
          ), null, { gap: 12 })}
          {panel('WHAT THEY TAPPED', (
            <div className="flex flex-col">
              {taps.length === 0 && <span style={{ fontSize: 12, color: MUTED }}>Nothing tapped yet.</span>}
              {taps.map(([k, n], i) => (
                <span key={k} className="flex justify-between" style={{ gap: 10, padding: '9px 0', borderTop: rule(i) }}>
                  <span style={{ fontSize: 10, letterSpacing: '.1em', color: MUTED }}>{k}</span><span style={{ fontSize: 13, fontWeight: 800 }}>{Math.round(n)}</span>
                </span>
              ))}
            </div>
          ), null, { gap: 8 })}
        </div>
      </div>
      <div className="grid items-stretch" style={{ gridTemplateColumns: mid ? 'minmax(0,1fr)' : '520px minmax(0,1fr)', gap }}>
        <section className="flex min-w-0 flex-col bg-white font-mono" style={{ borderRadius: r, padding: pad, gap: 14 }} data-testid="offer-funnel">
          <span style={lbl(12, '.16em')}>OFFER FUNNEL</span>
          {funnel.map(([k, n, w, p, c]) => (
            <div key={k} className="grid items-center" style={{ gridTemplateColumns: phone ? '88px minmax(0,1fr) 54px' : '120px minmax(0,1fr) 54px 64px', gap: 12 }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.1em' }}>{k}</span>
              <span className="overflow-hidden" style={{ height: 22, borderRadius: 999, background: CHIP }}><span className="block h-full" style={{ width: `${Math.max(2, w)}%`, borderRadius: 999, background: c }} /></span>
              <span className="text-right" style={{ fontSize: 14, fontWeight: 800 }}>{n}</span>
              {!phone && <span className="text-right" style={{ fontSize: 10, color: MUTED }}>{p}</span>}
            </div>
          ))}
          {note('Four numbers a kitchen can act on: how many saw it, how many took it, how many walked in with it, and what that cost.')}
        </section>
        {panel('WHEN THEY WALK IN WITH IT', (
          <>
            <div className="flex items-end" style={{ flex: 1, minHeight: 150, gap: 6 }}>
              {dayHours.map(([n, h]) => (
                <div key={h} className="flex min-w-0 flex-1 flex-col items-center justify-end" style={{ height: '100%', gap: 6 }}>
                  <span className="w-full" style={{ height: Math.max(3, Math.round((n / maxH) * 110)), borderRadius: 6, background: n > 0 && n === maxH ? MAGENTA : INK }} />
                  <span style={{ fontSize: 9, color: MUTED }}>{h}</span>
                </div>
              ))}
            </div>
            {note('Redemptions by hour. The point of this chart is scheduling — an offer that lands at 21:00 needs a different roster than one that lands at 17:00.')}
          </>
        ))}
      </div>
      {extras.length > 0 && (
        <div className="grid" style={{ gridTemplateColumns: `repeat(${phone ? 1 : 3}, minmax(0,1fr))`, gap }}>
          {extras.map(([t, rows]) => panel(t, (
            <div className="flex flex-col">
              {rows.map((s, i) => (
                <span key={s.key} className="flex justify-between" style={{ gap: 10, padding: '9px 0', borderTop: rule(i) }}>
                  <span style={{ fontSize: 10, letterSpacing: '.1em', color: MUTED }}>{s.label}</span><span style={{ fontSize: 13, fontWeight: 800 }}>{fmt(s)}</span>
                </span>
              ))}
            </div>
          ), null, { gap: 8 }))}
        </div>
      )}
      {/* Rich Analytics — the server decides (flag or subscription); locked pages get the plan panel. */}
      <RichAnalytics dash={dash} phone={phone} mid={mid} gap={gap} />
    </div>
  );
}
